/**
 * Analytics Debug & Test Mode Utilities
 *
 * Provides dev-friendly event inspection and Meta Test Events support.
 *
 * ── How to use ──────────────────────────────────────────────────────────────
 *
 * 1. **Console debug logging** (automatic in development):
 *    All events are logged to the console with `[Analytics]` prefix.
 *    Enable in production: `localStorage.setItem('prepframe_analytics_debug', '1')`
 *    Disable: `localStorage.removeItem('prepframe_analytics_debug')`
 *
 * 2. **Meta Test Events mode**:
 *    Set your test event code from Meta Events Manager → Test Events:
 *    `localStorage.setItem('prepframe_meta_test_code', 'TEST12345')`
 *    This appends `test_event_code` to CAPI payloads so events appear
 *    in the Test Events tab without polluting production data.
 *    Remove when done: `localStorage.removeItem('prepframe_meta_test_code')`
 *
 * 3. **Event history** (development only):
 *    `window.__PREPFRAME_EVENTS` — array of all events fired this session.
 *    Inspect in browser DevTools console.
 */

const isDev = import.meta.env.DEV;

// ── Debug logging ───────────────────────────────────────────────────────────

function isDebugEnabled(): boolean {
  if (isDev) return true;
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('prepframe_analytics_debug') === '1';
}

export interface DebugEvent {
  timestamp: string;
  event_name: string;
  event_id: string;
  channel: 'browser' | 'capi' | 'both';
  params?: Record<string, unknown>;
}

/** In-memory event log for dev inspection */
const eventHistory: DebugEvent[] = [];

if (typeof window !== 'undefined' && isDev) {
  (window as any).__PREPFRAME_EVENTS = eventHistory;
}

/**
 * Log an analytics event for debugging.
 * Called internally by the event helpers — not intended for direct use.
 */
export function debugLogEvent(
  event_name: string,
  event_id: string,
  channel: 'browser' | 'capi' | 'both',
  params?: Record<string, unknown>,
): void {
  if (!isDebugEnabled()) return;

  const entry: DebugEvent = {
    timestamp: new Date().toISOString(),
    event_name,
    event_id,
    channel,
    params,
  };

  eventHistory.push(entry);

  // Keep history bounded
  if (eventHistory.length > 200) eventHistory.splice(0, eventHistory.length - 200);

  const style = channel === 'browser'
    ? 'color: #1877F2; font-weight: bold'
    : channel === 'capi'
      ? 'color: #00C853; font-weight: bold'
      : 'color: #FF6D00; font-weight: bold';

  console.groupCollapsed(
    `%c[Analytics] ${event_name} (${channel})`,
    style,
  );
  console.log('event_id:', event_id);
  if (params && Object.keys(params).length > 0) {
    console.log('params:', params);
  }
  console.log('time:', entry.timestamp);
  console.groupEnd();
}

// ── Meta Test Events ────────────────────────────────────────────────────────

/**
 * Returns the Meta test_event_code if set, or undefined.
 * Used by the CAPI dispatcher to append to payloads.
 */
export function getMetaTestEventCode(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return localStorage.getItem('prepframe_meta_test_code') || undefined;
}

/**
 * Convenience: set the Meta test event code from the console.
 * Usage: setMetaTestCode('TEST12345')
 */
export function setMetaTestCode(code: string): void {
  localStorage.setItem('prepframe_meta_test_code', code);
  console.log(`[Analytics] Meta test_event_code set to "${code}". CAPI events will include this code.`);
}

/**
 * Convenience: clear the Meta test event code.
 */
export function clearMetaTestCode(): void {
  localStorage.removeItem('prepframe_meta_test_code');
  console.log('[Analytics] Meta test_event_code cleared. CAPI events will go to production.');
}

// Expose helpers on window for easy console access in dev
if (typeof window !== 'undefined' && isDev) {
  (window as any).setMetaTestCode = setMetaTestCode;
  (window as any).clearMetaTestCode = clearMetaTestCode;
}
